import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

const toAmPm = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hr = parseInt(h);
  const ampm = hr >= 12 ? 'PM' : 'AM';
  const hr12 = hr % 12 || 12;
  return `${hr12}:${m} ${ampm}`;
};

// Check if ALL event times for an announcement have passed
function allTimesPassed(event_time) {
  if (!event_time || !event_time.trim()) return false; // no time = never expires
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
  const times = event_time.split(',').map(t => t.trim()).filter(Boolean);
  // All times must have passed for it to be considered expired
  return times.every(t => {
    const dt = new Date(`${todayStr}T${t}:00`);
    return now > dt;
  });
}

export default function Announcement() {
  const [announcements, setAnnouncements] = useState([]);
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase
        .from('announcements')
        .select('*')
        .eq('active', true)
        .order('created_at', { ascending: false });
      if (data && data.length > 0) {
        // Filter out announcements whose event times have all passed
        const valid = data.filter(a => !allTimesPassed(a.event_time));
        // Auto-disable expired ones in DB
        const expired = data.filter(a => allTimesPassed(a.event_time));
        expired.forEach(a => {
          supabase.from('announcements').update({ active: false }).eq('id', a.id).then(() => {});
        });
        if (valid.length > 0) {
          setAnnouncements(valid);
          setTimeout(() => setVisible(true), 1500);
        }
      }
    };
    fetch();
  }, []);

  if (!visible || dismissed || announcements.length === 0) return null;
  const ann = announcements[current];

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9998,
      background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20, backdropFilter: 'blur(4px)',
      animation: 'fadeIn 0.4s ease',
    }}>
      <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}}`}</style>
      <div style={{
        maxWidth: 520, width: '100%',
        background: 'linear-gradient(160deg, rgba(13,27,62,0.98), rgba(7,15,34,0.99))',
        border: '1px solid rgba(201,168,76,0.4)',
        borderRadius: 20, padding: '40px 36px',
        position: 'relative', textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.7)',
      }}>
        <button onClick={() => setDismissed(true)} style={{
          position: 'absolute', top: 16, right: 16,
          background: 'rgba(255,255,255,0.08)', border: 'none',
          color: '#c8bfa8', width: 32, height: 32, borderRadius: '50%',
          fontSize: '1rem', cursor: 'pointer', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>✕</button>
        <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>📢</div>
        <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: '0.7rem', color: 'rgba(201,168,76,0.7)', letterSpacing: '0.3em', marginBottom: 16, textTransform: 'uppercase' }}>
          Church Announcement
        </div>
        <h3 style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: '1.1rem', color: '#c9a84c', marginBottom: 16 }}>
          {ann.title}
        </h3>
        {ann.event_time && (
          <div style={{ display:'flex', gap:8, justifyContent:'center', flexWrap:'wrap', marginBottom: 12 }}>
            {ann.event_time.split(', ').filter(Boolean).map((t,i) => (
              <span key={i} style={{ background:'rgba(201,168,76,0.15)', border:'1px solid rgba(201,168,76,0.4)', borderRadius:20, padding:'4px 12px', fontSize:'0.8rem', color:'#e8c97a' }}>
                🕐 {toAmPm(t)}
              </span>
            ))}
          </div>
        )}
        <p style={{ fontFamily: "'Crimson Pro',serif", fontSize: '1.05rem', color: '#c8bfa8', lineHeight: 1.7, marginBottom: 28 }}>
          {ann.message}
        </p>
        {announcements.length > 1 && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 20 }}>
            {announcements.map((_, i) => (
              <button key={i} onClick={() => setCurrent(i)} style={{
                width: i === current ? 24 : 8, height: 8, borderRadius: 4,
                border: 'none', background: i === current ? '#c9a84c' : 'rgba(201,168,76,0.25)',
                cursor: 'pointer', padding: 0, transition: 'all 0.3s',
              }} />
            ))}
          </div>
        )}
        <button onClick={() => setDismissed(true)} style={{
          background: 'linear-gradient(135deg,#c9a84c,#8b6914)',
          border: 'none', color: '#070f22',
          fontFamily: "'Raleway',sans-serif", fontWeight: 700,
          fontSize: '0.82rem', letterSpacing: '0.12em', textTransform: 'uppercase',
          padding: '12px 32px', borderRadius: 40, cursor: 'pointer',
        }}>
          God Bless You 🙏
        </button>
      </div>
    </div>
  );
}
